import { Info, RefreshCw, Search, TriangleAlert } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { MetricStrip } from '../components/MetricStrip'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { Pagination } from '../components/Pagination'
import { ProductTable } from '../components/ProductTable'
import { RealtimeProductAnalysisTable } from '../components/RealtimeProductAnalysisTable'
import { useProductData } from '../data'
import { JD_STORE_ID, productMasterCostDefaults, productMasterRecordMap } from '../productMaster'
import { internalProductRecordsByCode, summarizeProductProfit } from '../productProfit'
import { type RealtimeProductSortKey, useRealtimeData } from '../realtime'
import {
  activeDatesForSkuTag,
  aggregateSkuProducts,
  badgesForProduct,
  productWithAllowedDates,
  skuMatchesTag,
  type ProductBadge,
} from '../segments'
import { statisticsPeriodOptions, statisticsWindow, type StatisticsPeriod, visibleDateRange } from '../statistics'
import type { ProductLevel, ProductRecord, SortKey } from '../types'
import {
  categoryName,
  integer,
  isActive,
  itemUnitPrice,
  metricsAcrossDates,
  metricsOn,
  money,
  percent,
  previousDateOf,
  sumMetrics,
} from '../utils'

type ActivityScope = 'pool' | 'active'
type ListStatisticsPeriod = StatisticsPeriod | 'realtime'

const listStatisticsPeriodOptions: Array<{ key: ListStatisticsPeriod, label: string }> = [
  { key: 'realtime', label: '历史快照' },
  ...statisticsPeriodOptions,
]

interface TaggedProductView {
  products: ProductRecord[]
  badgesByProductId: Map<string, ProductBadge[]>
  matchTextByProductId: Map<string, string>
  matchedSkuCountByProductId: Map<string, number>
  skuProductsByProductId: Map<string, ProductRecord[]>
}

export function ProductListPage({ level }: { level: ProductLevel }) {
  const { data, segments, segmentIndex, selectedDate, productIndex, productMaster, internalProductInfo, erpInventory } = useProductData()
  const { data: realtimeData, loading: realtimeLoading, error: realtimeError, reload: reloadRealtime } = useRealtimeData()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('all')
  const [status, setStatus] = useState('all')
  const [sortKey, setSortKey] = useState<SortKey>('gmv')
  const [realtimeSortKey, setRealtimeSortKey] = useState<RealtimeProductSortKey>('gmv')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [statisticsPeriod, setStatisticsPeriod] = useState<ListStatisticsPeriod>(() => {
    const requestedPeriod = searchParams.get('period')
    return listStatisticsPeriodOptions.some((item) => item.key === requestedPeriod)
      ? requestedPeriod as ListStatisticsPeriod
      : 'day'
  })
  const [fulfillmentTag, setFulfillmentTag] = useState(() => searchParams.get('fulfillment') || '')
  const [promotionTag, setPromotionTag] = useState('')
  const [activityScope, setActivityScope] = useState<ActivityScope>('pool')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const isRealtime = statisticsPeriod === 'realtime'

  useEffect(() => {
    const requestedPeriod = searchParams.get('period')
    setStatisticsPeriod(
      listStatisticsPeriodOptions.some((item) => item.key === requestedPeriod)
        ? requestedPeriod as ListStatisticsPeriod
        : 'day',
    )
  }, [searchParams])

  const allProducts = useMemo(() => data?.products.filter((product) => product.level === level) || [], [data, level])
  const allSkus = useMemo(() => data?.products.filter((product) => product.level === 'sku') || [], [data])
  const skuById = useMemo(() => new Map(allSkus.map((product) => [product.id, product])), [allSkus])
  const productMasterRecords = useMemo(() => productMasterRecordMap(productMaster, JD_STORE_ID), [productMaster])
  const costDefaults = useMemo(() => productMasterCostDefaults(productMaster, JD_STORE_ID), [productMaster])
  const companyRecordsByCode = useMemo(
    () => internalProductRecordsByCode(internalProductInfo?.records || []),
    [internalProductInfo],
  )
  const erpAvailableByMerchantSku = useMemo(
    () => new Map((erpInventory?.records || []).map((record) => [record.productCode.trim(), record.available])),
    [erpInventory],
  )
  const realtimeProducts = useMemo(
    () => realtimeData?.products.filter((product) => product.level === level) || [],
    [level, realtimeData],
  )
  const categories = useMemo(
    () => Array.from(new Set(
      isRealtime
        ? realtimeProducts.map((product) => product.category[2] || product.category[1] || product.category[0] || '未分类')
        : allProducts.map(categoryName),
    )).sort(),
    [allProducts, isRealtime, realtimeProducts],
  )
  const fulfillmentTags = useMemo(() => segments?.tags.filter((tag) => tag.dimensionId === 'fulfillment') || [], [segments])
  const promotionTags = useMemo(() => segments?.tags.filter((tag) => tag.dimensionId === 'promotion') || [], [segments])
  const period = useMemo(
    () => data ? statisticsWindow(isRealtime ? 'day' : statisticsPeriod, selectedDate, data.meta.dates) : null,
    [data, selectedDate, statisticsPeriod],
  )
  const previousDate = data ? previousDateOf(data.meta.dates, selectedDate) : ''
  const showDelta = !isRealtime && statisticsPeriod === 'day' && !(promotionTag && activityScope === 'active')
  const effectiveSortKey = !showDelta && sortKey === 'delta' ? 'gmv' : sortKey
  const hasTagFilter = Boolean(fulfillmentTag || promotionTag)

  const taggedView = useMemo<TaggedProductView>(() => {
    const badgesByProductId = new Map<string, ProductBadge[]>()
    const matchTextByProductId = new Map<string, string>()
    const matchedSkuCountByProductId = new Map<string, number>()
    const skuProductsByProductId = new Map<string, ProductRecord[]>()
    if (!data || !period) return { products: [], badgesByProductId, matchTextByProductId, matchedSkuCountByProductId, skuProductsByProductId }

    const selectedTagIds = [fulfillmentTag, promotionTag].filter(Boolean)
    const badgesForRow = (product: ProductRecord, matchedSkuCount?: number, totalSkuCount?: number) => {
      const badges = badgesForProduct(segmentIndex, product, selectedDate)
      for (const tagId of selectedTagIds) {
        if (badges.some((badge) => badge.id === tagId)) continue
        const tag = segmentIndex.tagsById.get(tagId)
        if (tag) badges.push({ id: tag.id, label: tag.name, tone: tag.tone, title: tag.description })
      }
      badgesByProductId.set(product.id, badges)
      if (matchedSkuCount !== undefined && totalSkuCount !== undefined) {
        matchedSkuCountByProductId.set(product.id, matchedSkuCount)
        matchTextByProductId.set(
          product.id,
          `命中 ${matchedSkuCount}/${totalSkuCount} 个官方SKU${hasTagFilter ? ' · 仅汇总命中SKU' : ''}`,
        )
      }
    }

    if (!hasTagFilter) {
      for (const product of allProducts) {
        badgesForRow(product)
        skuProductsByProductId.set(
          product.id,
          product.level === 'sku'
            ? [product]
            : (segmentIndex.skuIdsBySpu.get(product.id) || [])
              .map((skuId) => skuById.get(skuId))
              .filter((item): item is ProductRecord => Boolean(item)),
        )
      }
      return { products: allProducts, badgesByProductId, matchTextByProductId, matchedSkuCountByProductId, skuProductsByProductId }
    }

    const matchingSku = (sku: ProductRecord) =>
      skuMatchesTag(segmentIndex, sku.id, fulfillmentTag, period.dates)
      && skuMatchesTag(segmentIndex, sku.id, promotionTag, period.dates)

    const allowedDates = (sku: ProductRecord) => {
      if (!promotionTag || activityScope === 'pool') return new Set(period.dates)
      return new Set(activeDatesForSkuTag(segmentIndex, sku.id, promotionTag, period.dates))
    }

    if (level === 'sku') {
      const products = allProducts.flatMap((product) => {
        if (!matchingSku(product)) return []
        const dates = allowedDates(product)
        if (!dates.size) return []
        const visibleProduct = promotionTag && activityScope === 'active'
          ? productWithAllowedDates(product, dates)
          : product
        badgesForRow(visibleProduct)
        matchedSkuCountByProductId.set(product.id, 1)
        skuProductsByProductId.set(product.id, [visibleProduct])
        return [visibleProduct]
      })
      return { products, badgesByProductId, matchTextByProductId, matchedSkuCountByProductId, skuProductsByProductId }
    }

    const products = allProducts.flatMap((spu) => {
      const childSkuIds = segmentIndex.skuIdsBySpu.get(spu.id) || []
      const childSkus = childSkuIds.map((id) => skuById.get(id)).filter((item): item is ProductRecord => Boolean(item))
      const matchedSkus = childSkus.filter(matchingSku)
      if (!matchedSkus.length) return []
      const allowedDatesBySku = promotionTag && activityScope === 'active'
        ? new Map(matchedSkus.map((sku) => [sku.id, allowedDates(sku)]))
        : undefined
      const visibleProduct = aggregateSkuProducts(spu, matchedSkus, allowedDatesBySku)
      badgesForRow(visibleProduct, matchedSkus.length, childSkus.length)
      skuProductsByProductId.set(
        spu.id,
        allowedDatesBySku
          ? matchedSkus.map((sku) => productWithAllowedDates(sku, allowedDatesBySku.get(sku.id) || new Set()))
          : matchedSkus,
      )
      return [visibleProduct]
    })
    return { products, badgesByProductId, matchTextByProductId, matchedSkuCountByProductId, skuProductsByProductId }
  }, [
    activityScope,
    allProducts,
    data,
    fulfillmentTag,
    hasTagFilter,
    level,
    period,
    promotionTag,
    segmentIndex,
    selectedDate,
    skuById,
  ])

  const profitView = useMemo(() => {
    const profitByProductId = new Map<string, number | null>()
    const profitMarginByProductId = new Map<string, number | null>()
    const profitContextByProductId = new Map<string, string>()
    const summariesByProductId = new Map<string, ReturnType<typeof summarizeProductProfit>>()
    if (!period) return { profitByProductId, profitMarginByProductId, profitContextByProductId, summariesByProductId }
    for (const product of taggedView.products) {
      const summary = summarizeProductProfit(
        taggedView.skuProductsByProductId.get(product.id) || [],
        period.dates,
        selectedDate,
        productMasterRecords,
        costDefaults,
        companyRecordsByCode,
        segmentIndex,
      )
      summariesByProductId.set(product.id, summary)
      profitByProductId.set(product.id, summary.profitWithRebate)
      profitMarginByProductId.set(product.id, summary.profitWithRebateMargin)
      profitContextByProductId.set(product.id, summary.context)
    }
    return { profitByProductId, profitMarginByProductId, profitContextByProductId, summariesByProductId }
  }, [
    companyRecordsByCode,
    costDefaults,
    period,
    productMasterRecords,
    segmentIndex,
    selectedDate,
    taggedView.products,
    taggedView.skuProductsByProductId,
  ])

  const productRows = useMemo(() => {
    if (!period) return []
    return taggedView.products.map((product) => ({
      product,
      current: metricsAcrossDates(product, period.dates),
      previous: metricsOn(product, previousDate),
    }))
  }, [period, previousDate, taggedView.products])

  const filteredRows = useMemo(() => {
    if (!period?.dates.length) return []
    const needle = search.trim().toLowerCase()
    return productRows.filter(({ product, current }) => {
      const matchesSearch = !needle || [product.name, product.nameNote || '', product.sourceName, product.id, product.jdSkuId, product.merchantSku]
        .some((value) => value.toLowerCase().includes(needle))
      const matchesCategory = category === 'all' || categoryName(product) === category
      const active = isActive(current)
      const matchesStatus = status === 'all' || (status === 'active' ? active : !active)
      return matchesSearch && matchesCategory && matchesStatus
    }).sort((a, b) => {
      const compareNullable = (left: number | null | undefined, right: number | null | undefined) => {
        const leftMissing = left === null || left === undefined
        const rightMissing = right === null || right === undefined
        if (leftMissing && rightMissing) return a.product.id.localeCompare(b.product.id)
        if (leftMissing) return 1
        if (rightMissing) return -1
        const result = left - right
        if (result) return sortDirection === 'asc' ? result : -result
        return a.product.id.localeCompare(b.product.id)
      }
      if (effectiveSortKey === 'profitWithRebate') {
        return compareNullable(
          profitView.profitByProductId.get(a.product.id),
          profitView.profitByProductId.get(b.product.id),
        )
      }
      if (effectiveSortKey === 'profitWithRebateMargin') {
        return compareNullable(
          profitView.profitMarginByProductId.get(a.product.id),
          profitView.profitMarginByProductId.get(b.product.id),
        )
      }
      const value = (row: typeof a) => effectiveSortKey === 'delta'
        ? row.current.gmv - row.previous.gmv
        : effectiveSortKey === 'averageItemPrice'
          ? itemUnitPrice(row.current.gmv, row.current.soldItemCount) || 0
          : row.current[effectiveSortKey]
      const result = value(a) - value(b)
      if (result) return sortDirection === 'asc' ? result : -result
      return a.product.id.localeCompare(b.product.id)
    })
  }, [category, effectiveSortKey, period, productRows, profitView.profitByProductId, profitView.profitMarginByProductId, search, sortDirection, status])

  const filtered = useMemo(() => filteredRows.map((row) => row.product), [filteredRows])
  const filteredSummary = useMemo(() => sumMetrics(filteredRows.map((row) => row.current)), [filteredRows])
  const filteredProfitSummary = useMemo(() => {
    const summaries = filtered
      .map((product) => profitView.summariesByProductId.get(product.id))
      .filter((summary): summary is NonNullable<typeof summary> => Boolean(summary))
    const activeSkuCount = summaries.reduce((sum, summary) => sum + summary.activeSkuCount, 0)
    const coveredSkuCount = summaries.reduce((sum, summary) => sum + summary.coveredSkuCount, 0)
    const profitWithRebate = summaries.reduce(
      (sum, summary) => sum + (summary.profitWithRebate || 0),
      0,
    )
    const coveredGmv = summaries.reduce((sum, summary) => sum + summary.coveredGmv, 0)
    return {
      activeSkuCount,
      coveredSkuCount,
      profitWithRebate,
      profitWithRebateMargin: coveredGmv > 0 ? profitWithRebate / coveredGmv : null,
    }
  }, [filtered, profitView.summariesByProductId])
  const filteredSpuCount = useMemo(() => level === 'spu'
    ? filtered.length
    : new Set(filtered.map((product) => product.spuId).filter(Boolean)).size, [filtered, level])
  const filteredSkuCount = useMemo(() => level === 'sku'
    ? filtered.length
    : filtered.reduce((total, product) => total + (taggedView.matchedSkuCountByProductId.get(product.id) || 0), 0),
  [filtered, level, taggedView.matchedSkuCountByProductId])

  const realtimeBadgesByProductId = useMemo(() => {
    const result = new Map<string, ProductBadge[]>()
    if (!realtimeData) return result
    for (const row of realtimeProducts) {
      const record = productIndex.get(`${row.level}:${row.id}`)
      if (record) result.set(row.id, badgesForProduct(segmentIndex, record, realtimeData.meta.businessDate))
    }
    return result
  }, [productIndex, realtimeData, realtimeProducts, segmentIndex])

  const realtimeFilteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return realtimeProducts
      .filter((product) => {
        const record = productIndex.get(`${product.level}:${product.id}`)
        const matchesSearch = !needle || [
          product.name,
          product.sourceName,
          product.id,
          product.merchantSku || '',
          record?.nameNote || '',
          record?.merchantSku || '',
        ].some((value) => value.toLowerCase().includes(needle))
        const productCategory = product.category[2] || product.category[1] || product.category[0] || '未分类'
        const matchesCategory = category === 'all' || productCategory === category
        const active = product.metrics.gmv > 0 || product.metrics.soldItemCount > 0
        const matchesStatus = status === 'all' || (status === 'active' ? active : !active)
        return matchesSearch && matchesCategory && matchesStatus
      })
      .sort((a, b) => {
        const value = (product: typeof a) => realtimeSortKey === 'delta'
          ? product.metrics.gmv - (product.previousMetrics?.gmv || 0)
          : product.metrics[realtimeSortKey]
        const result = Number(value(a) || 0) - Number(value(b) || 0)
        if (result) return sortDirection === 'asc' ? result : -result
        return a.id.localeCompare(b.id)
      })
  }, [category, productIndex, realtimeProducts, realtimeSortKey, search, sortDirection, status])

  useEffect(
    () => setPage(1),
    [
      level,
      search,
      category,
      status,
      sortKey,
      sortDirection,
      realtimeSortKey,
      statisticsPeriod,
      selectedDate,
      pageSize,
      fulfillmentTag,
      promotionTag,
      activityScope,
    ],
  )
  const resultCount = isRealtime ? realtimeFilteredRows.length : filtered.length
  const pageCount = Math.max(1, Math.ceil(resultCount / pageSize))
  const currentPage = Math.min(page, pageCount)
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const pagedRealtime = realtimeFilteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const activeCount = isRealtime
    ? realtimeProducts.filter((product) => product.metrics.gmv > 0 || product.metrics.soldItemCount > 0).length
    : productRows.filter((row) => isActive(row.current)).length
  const statusPrefix = isRealtime ? '快照' : statisticsPeriod !== 'day' ? '区间' : '昨日'

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDirection((direction) => direction === 'desc' ? 'asc' : 'desc')
    else { setSortKey(key); setSortDirection('desc') }
  }
  const handleRealtimeSort = (key: RealtimeProductSortKey) => {
    if (key === realtimeSortKey) setSortDirection((direction) => direction === 'desc' ? 'asc' : 'desc')
    else { setRealtimeSortKey(key); setSortDirection('desc') }
  }
  const handleStatisticsPeriod = (value: ListStatisticsPeriod) => {
    setStatisticsPeriod(value)
    const nextParams = new URLSearchParams(searchParams)
    if (value === 'day') nextParams.delete('period')
    else nextParams.set('period', value)
    setSearchParams(nextParams, { replace: true })
    if (value === 'realtime') {
      setFulfillmentTag('')
      setPromotionTag('')
      setCategory('all')
      setStatus('all')
      setRealtimeSortKey('gmv')
      setSortDirection('desc')
    } else if (value !== 'day' && sortKey === 'delta') {
      setSortKey('gmv')
      setSortDirection('desc')
    }
  }

  if (!data || !segments || !period) return null
  const latestRealtimeSnapshot = realtimeData?.snapshots[realtimeData.snapshots.length - 1]
  const realtimePreviousAvailable = Boolean(realtimeData?.previousSummary)
  const realtimeSnapshotTime = latestRealtimeSnapshot
    ? new Date(latestRealtimeSnapshot.snapshotAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
    : '等待数据'
  return (
    <div className="page-stack">
      <div className="page-title-row">
        <div>
          <h1>{level.toUpperCase()} 商品</h1>
          <p>
            {isRealtime
              ? `历史快照 · ${realtimeData?.meta.businessDate || '暂无日期'} ${realtimeSnapshotTime} · 当前商品池 ${realtimeProducts.length.toLocaleString('zh-CN')} 个${level.toUpperCase()} · 快照动销 ${activeCount.toLocaleString('zh-CN')}`
              : `${period.label} · ${visibleDateRange(period.dates)} · 当前商品池 ${taggedView.products.length.toLocaleString('zh-CN')} 个${level.toUpperCase()} · ${statusPrefix}动销 ${activeCount.toLocaleString('zh-CN')}`}
          </p>
        </div>
        <div className="page-title-actions">
          <DataFreshnessBadge
            date={isRealtime ? realtimeData?.meta.businessDate : data.meta.maxDate}
            label={isRealtime ? '快照数据截至' : '商品数据截至'}
            mode={isRealtime ? 'paused' : 'daily'}
          />
          {level === 'sku' && (
            <DataFreshnessBadge
              date={erpInventory?.source.exportedAt}
              label="ERP库存截至"
              mode="on-demand"
            />
          )}
          <button
            type="button"
            className="refresh-button"
            onClick={() => isRealtime ? void reloadRealtime() : window.location.reload()}
          >
            <RefreshCw aria-hidden="true" />{isRealtime ? '刷新本地快照' : '刷新数据'}
          </button>
        </div>
      </div>

      <section className="list-period-panel">
        <div className="section-heading-row period-heading">
          <div>
            <h2>统计时间范围</h2>
            <p>{isRealtime
              ? `${realtimeData?.meta.businessDate || '暂无日期'} ${realtimeSnapshotTime} · ${realtimeData?.meta.snapshotCount || 0} 份只读快照`
              : `${period.label} · ${visibleDateRange(period.dates)} · ${period.complete ? `完整${period.requestedDayCount}天` : `仅覆盖${period.dates.length}/${period.requestedDayCount}天`}`}
            </p>
          </div>
          <div className="metric-switch" role="group" aria-label={`${level.toUpperCase()}统计时间范围`}>
            {listStatisticsPeriodOptions.map((item) => (
              <button
                type="button"
                key={item.key}
                className={item.key === statisticsPeriod ? 'is-active' : ''}
                aria-pressed={item.key === statisticsPeriod}
                onClick={() => handleStatisticsPeriod(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        {isRealtime && (
          <div className="realtime-list-notices">
            <p className="realtime-inline-notice">
              <Info aria-hidden="true" />
              历史实时快照已暂停更新，不代表当前经营情况；需要新数据时，在频道说一声由AI按需采集。利润、退款和活动期仍使用完整自然日口径。
            </p>
            {level === 'sku' && realtimeData?.meta.skuExportLimitReached && (
              <p className="realtime-inline-warning">
                <TriangleAlert aria-hidden="true" />
                SKU实时导出达到京东1000行上限，当前排行不表述为全量SKU覆盖。
              </p>
            )}
            {realtimeError && <p className="realtime-inline-warning">{realtimeError}</p>}
            {realtimeLoading && !realtimeData && <p className="realtime-inline-notice">正在加载实时商品快照…</p>}
          </div>
        )}
        {!isRealtime && !period.complete && (
          <p className="period-coverage-warning">
            请求范围为 {period.startDate} 至 {period.endDate}，当前滚动数据只能覆盖 {visibleDateRange(period.dates)}；以下为部分区间汇总。
          </p>
        )}
      </section>

      <section className="segment-filter-panel">
        <div className="filter-bar">
          <label className="search-control">
            <span className="sr-only">搜索商品</span><Search aria-hidden="true" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={level === 'spu' ? '搜索名称、备注或SPU编码' : `搜索款式名或${level.toUpperCase()}编码`} />
          </label>
          <label>
            <span className="sr-only">仓储方式</span>
            <select value={fulfillmentTag} onChange={(event) => setFulfillmentTag(event.target.value)} disabled={isRealtime}>
              <option value="">{isRealtime ? '实时暂不筛仓储方式' : '全部仓储方式'}</option>
              {fulfillmentTags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}
            </select>
          </label>
          <label>
            <span className="sr-only">活动标签</span>
            <select value={promotionTag} onChange={(event) => setPromotionTag(event.target.value)} disabled={isRealtime}>
              <option value="">{isRealtime ? '实时暂不筛活动标签' : '全部活动标签'}</option>
              {promotionTags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}
            </select>
          </label>
          <label><span className="sr-only">类目</span><select value={category} onChange={(event) => setCategory(event.target.value)}><option value="all">全部类目</option>{categories.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
          <label><span className="sr-only">动销状态</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">全部状态</option><option value="active">{statusPrefix}动销</option><option value="inactive">{statusPrefix}无成交</option></select></label>
          <label>
            <span className="sr-only">排序</span>
            {isRealtime ? (
              <select value={realtimeSortKey} onChange={(event) => { setRealtimeSortKey(event.target.value as RealtimeProductSortKey); setSortDirection('desc') }}>
                <option value="gmv">按实时成交金额排序</option>
                <option value="soldItemCount">按成交件数排序</option>
                {level === 'sku' && <option value="averageItemPrice">按件单价排序</option>}
                <option value="productVisitors">按商品访客排序</option>
                <option value="productPageviews">按浏览量排序</option>
                <option value="conversionRate">按转化率排序</option>
                <option value="averageOrderValue">按客单价排序</option>
                {realtimePreviousAvailable && <option value="delta">按较上次变化排序</option>}
              </select>
            ) : (
              <select value={effectiveSortKey} onChange={(event) => { setSortKey(event.target.value as SortKey); setSortDirection('desc') }}>
                <option value="gmv">按成交金额排序</option>
                <option value="profitWithRebate">按含返点利润排序</option>
                <option value="profitWithRebateMargin">按含返点利润率排序</option>
                <option value="soldItemCount">按成交件数排序</option>
                {level === 'sku' && <option value="averageItemPrice">按件单价排序</option>}
                <option value="productVisitors">按访客排序</option>
                <option value="conversionRate">按转化率排序</option>
                {showDelta && <option value="delta">按较前日变化排序</option>}
              </select>
            )}
          </label>
        </div>
        {promotionTag && (
          <div className="activity-scope-row">
            <span>活动数据口径</span>
            <div className="metric-switch" role="group" aria-label="活动数据口径">
              <button type="button" className={activityScope === 'pool' ? 'is-active' : ''} onClick={() => setActivityScope('pool')}>商品池数据</button>
              <button type="button" className={activityScope === 'active' ? 'is-active' : ''} onClick={() => setActivityScope('active')}>仅活动期</button>
            </div>
            <p>{activityScope === 'pool'
              ? '统计所选区间内这些活动商品的全部销售，不代表每笔成交都使用了活动优惠。'
              : '每个SKU仅统计其标记有效日期与所选时间范围的交集。'}</p>
          </div>
        )}
      </section>

      {hasTagFilter && (
        <section className="segment-result-summary">
          <div className="section-heading-row">
            <div>
              <h2>筛选商品汇总</h2>
              <p>{filteredSpuCount.toLocaleString('zh-CN')} 个SPU · {filteredSkuCount.toLocaleString('zh-CN')} 个SKU · 不重复汇总命中SKU</p>
            </div>
          </div>
          <MetricStrip items={[
            { label: '成交金额', value: money(filteredSummary.gmv), context: visibleDateRange(period.dates) },
            {
              label: '含返点利润',
              value: filteredProfitSummary.activeSkuCount === 0 || filteredProfitSummary.coveredSkuCount > 0
                ? money(filteredProfitSummary.profitWithRebate)
                : '—',
              context: filteredProfitSummary.activeSkuCount
                ? `已覆盖 ${filteredProfitSummary.coveredSkuCount}/${filteredProfitSummary.activeSkuCount} 个成交SKU`
                : '当前范围无成交SKU',
            },
            {
              label: '含返点利润率',
              value: filteredProfitSummary.profitWithRebateMargin === null
                ? '—'
                : percent(filteredProfitSummary.profitWithRebateMargin * 100),
              context: '含返点利润 ÷ 已覆盖成交金额',
            },
            { label: '成交件数', value: integer(filteredSummary.soldItemCount) },
            { label: '访客', value: integer(filteredSummary.productVisitors), context: '按SKU分天访客累加' },
            { label: '转化率', value: percent(filteredSummary.conversionRate), context: '累计成交客户 ÷ 累计访客' },
            { label: '客单价', value: money(filteredSummary.averageOrderValue) },
            { label: '取消退款', value: money(filteredSummary.cancelRefundAmount) },
          ]} />
        </section>
      )}

      {isRealtime ? (
        <RealtimeProductAnalysisTable
          products={pagedRealtime}
          productRecords={productIndex}
          sortKey={realtimeSortKey}
          sortDirection={sortDirection}
          onSort={handleRealtimeSort}
          showAverageItemPrice={level === 'sku'}
          badgesByProductId={realtimeBadgesByProductId}
          erpAvailableByMerchantSku={level === 'sku' ? erpAvailableByMerchantSku : undefined}
          emptyMessage={realtimeLoading ? '正在加载实时商品快照…' : '没有符合当前筛选条件的实时商品。'}
        />
      ) : (
        <ProductTable
          products={paged}
          selectedDate={selectedDate}
          dates={data.meta.dates}
          sortKey={effectiveSortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          metricDates={period.dates}
          showDelta={showDelta}
          showAverageItemPrice={level === 'sku'}
          badgesByProductId={taggedView.badgesByProductId}
          matchTextByProductId={taggedView.matchTextByProductId}
          profitByProductId={profitView.profitByProductId}
          profitMarginByProductId={profitView.profitMarginByProductId}
          profitContextByProductId={profitView.profitContextByProductId}
          erpAvailableByMerchantSku={level === 'sku' ? erpAvailableByMerchantSku : undefined}
          detailSearch={statisticsPeriod === 'day' ? '' : `?period=${encodeURIComponent(statisticsPeriod)}`}
          emptyMessage={!period.dates.length ? '所选统计范围暂无可用数据。' : '没有符合当前筛选条件的商品。'}
        />
      )}
      <Pagination page={currentPage} pageCount={pageCount} total={resultCount} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
    </div>
  )
}
